// PolygonEraser.ts
import { SimpleGeometry } from 'ol/geom';
import { Coordinate } from 'ol/coordinate';
import { euclideanDistance, findClosestPointIndex } from '../../../utils';

/**
 * PolygonEraser
 * - Treats each ring as a circular array of N distinct vertices (closing point removed)
 * - Decides which of the two arcs between firstIndex and lastIndex contains inBetweenIndex
 * - Removes that arc (keeping first and last vertices)
 * - Validates result: must leave at least 3 distinct vertices (plus closing point)
 */
class PolygonEraser {
  eraseFromPolygon(
    geometry: SimpleGeometry,
    firstPoint: Coordinate,
    inBetweenPoint: Coordinate,
    lastPoint: Coordinate,
  ): Coordinate[][] | undefined {
    const coordinates: Coordinate[][] = geometry.getCoordinates();

    // Find the ring (outer or hole) that contains the inBetweenPoint by closest point
    let targetRingIndex = 0;
    let minDistance = Infinity;

    for (let ringIndex = 0; ringIndex < coordinates.length; ringIndex++) {
      const ring = coordinates[ringIndex];
      const closestIndex = findClosestPointIndex(ring, inBetweenPoint);
      const distance = euclideanDistance(ring[closestIndex], inBetweenPoint);

      if (distance < minDistance) {
        minDistance = distance;
        targetRingIndex = ringIndex;
      }
    }

    const targetRing = coordinates[targetRingIndex];

    // Find indices in that ring (closest vertex indices)
    const firstIndex = findClosestPointIndex(targetRing, firstPoint);
    const lastIndex = findClosestPointIndex(targetRing, lastPoint);
    const inBetweenIndex = findClosestPointIndex(targetRing, inBetweenPoint);

    const newRing = this.removePolygonSection(targetRing, firstIndex, inBetweenIndex, lastIndex);

    console.log(
      `Polygon erasing: targeting ring ${targetRingIndex} (${targetRingIndex === 0 ? 'outer ring' : 'hole'})`,
      `first index ${firstIndex}, last index ${lastIndex}, inBetween index ${inBetweenIndex}`,
      `new ring length ${newRing?.length || 'undefined'} (was ${targetRing.length})`,
    );

    if (!newRing) {
      return undefined;
    }

    const newCoordinates = coordinates.map((ring, idx) => (idx === targetRingIndex ? newRing : ring));

    return newCoordinates;
  }

  /**
   * Remove the section of a polygon ring that contains the inBetweenIndex.
   * - `ring` may include a closing point equal to ring[0]. We will drop it for processing.
   * - Returns a new ring that is properly closed (first === last) and has at least 3 vertices
   * - Returns undefined if removal would produce an invalid polygon (less than 3 distinct vertices)
   */
  private removePolygonSection(
    ring: Coordinate[],
    firstIndex: number,
    inBetweenIndex: number,
    lastIndex: number,
  ): Coordinate[] | undefined {
    // If ring is too small, bail out
    if (!ring || ring.length < 4) {
      return undefined; // need at least 3 + closing
    }

    // Remove the closing duplicate if present
    const isClosed = this.coordinatesEqual(ring[0], ring[ring.length - 1]);
    const base = isClosed ? ring.slice(0, -1) : ring.slice();
    const N = base.length;

    if (N < 3) {
      return undefined;
    }

    // Normalize incoming indices into [0..N-1]
    const norm = (idx: number) => {
      if (idx < 0) {
        return ((idx % N) + N) % N;
      }

      return idx >= N ? idx % N : idx;
    };

    const A = norm(firstIndex);
    const B = norm(lastIndex);
    const X = norm(inBetweenIndex);

    // If first and last are the same vertex, nothing to remove
    if (A === B) {
      return undefined;
    }

    // Helper: are A and B adjacent in circular sense? If yes, nothing to remove
    const adjacent = (u: number, v: number) => (u + 1) % N === v || (v + 1) % N === u;

    if (adjacent(A, B)) {
      return undefined;
    }

    // Circular 'in arc' check: is X strictly between A->B moving forward (exclusive)
    const inArc = (from: number, x: number, to: number) => {
      if (from < to) {
        return x > from && x < to;
      }

      // wrapping case
      return x > from || x < to;
    };

    const xInDirect = inArc(A, X, B);

    // Build the kept-index list depending on which arc contains X
    const keepIndices: number[] = [];

    if (xInDirect) {
      // Remove the direct arc (A -> ... -> B), so keep B..A (inclusive)
      let i = B;

      do {
        keepIndices.push(i);
        i = (i + 1) % N;
      } while (i !== A);

      keepIndices.push(A);
    } else {
      // Remove the wrapping arc (B -> ... -> A), so keep A..B (inclusive)
      let i = A;

      do {
        keepIndices.push(i);
        i = (i + 1) % N;
      } while (i !== B);

      keepIndices.push(B);
    }

    // Rebuild coordinates from kept indices
    const newCoords = keepIndices.map((idx) => base[idx]);

    // Validate result must have at least 3 distinct vertices
    if (newCoords.length < 3) {
      return undefined;
    }

    // Ensure closure
    if (!this.coordinatesEqual(newCoords[0], newCoords[newCoords.length - 1])) {
      newCoords.push([...newCoords[0]]);
    }

    // After closure, final length must be >= 4 (3 distinct verts + closing)
    if (newCoords.length < 4) {
      return undefined;
    }

    return newCoords;
  }

  private coordinatesEqual(coord1: Coordinate, coord2: Coordinate): boolean {
    return coord1[0] === coord2[0] && coord1[1] === coord2[1];
  }
}

export const polygonEraser = new PolygonEraser();
